import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Search, Star, MapPin, Phone, Mail, Globe, ChevronRight,
  ArrowLeft, MessageSquare, Send, X, Filter, Award, Building2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  "Sonorisation": "volume-2", "Photo & Video": "camera", "Traiteur": "utensils-crossed",
  "Decoration": "palette", "Animation": "party-popper", "Securite": "shield-check",
  "Location materiel": "truck", "Communication": "megaphone", "Logistique": "boxes",
};

export default function OrgMarketplacePage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showContact, setShowContact] = useState(false);
  const [contactMsg, setContactMsg] = useState("");
  const [sending, setSending] = useState(false);

  const fetchProviders = useCallback(async () => {
    try {
      const params = selectedCategory ? `?category=${encodeURIComponent(selectedCategory)}` : "";
      const res = await axios.get(`${API}/api/event/marketplace${params}`, { headers });
      setProviders(res.data.providers || []);
      if (res.data.categories) setCategories(res.data.categories);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally { setLoading(false); }
  }, [token, selectedCategory]);

  useEffect(() => { fetchProviders(); }, [fetchProviders]);

  const sendContactRequest = async () => {
    if (!contactMsg.trim() || !selectedProvider) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/event/marketplace/${selectedProvider.id}/contact`, {
        message: contactMsg,
      }, { headers });
      toast.success("Demande de contact envoyee !");
      setShowContact(false);
      setContactMsg("");
    } catch { toast.error("Erreur lors de l'envoi"); }
    finally { setSending(false); }
  };

  const filtered = providers.filter(p =>
    `${p.name} ${p.description} ${p.city} ${(p.services || []).join(" ")}`.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0099CC]" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="marketplace-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Marketplace Prestataires</h1>
        <p className="text-[#64748b] mt-1">Trouvez et contactez les meilleurs professionnels pour vos evenements</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4A5568]" />
          <Input data-testid="marketplace-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher un prestataire, service, ville..."
            className="pl-10 bg-[#0B0F19] border-[#2A3441] text-white" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setSelectedCategory("")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${!selectedCategory ? "bg-[#0099CC] text-white" : "bg-[#151B2B] border border-[#2A3441] text-[#94A3B8] hover:text-white"}`}
            data-testid="filter-all"
          >
            Tous
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(selectedCategory === cat ? "" : cat)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedCategory === cat ? "bg-[#0099CC] text-white" : "bg-[#151B2B] border border-[#2A3441] text-[#94A3B8] hover:text-white"}`}
              data-testid={`filter-${cat}`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Provider Detail Modal */}
      {selectedProvider && !showContact && (
        <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-6 space-y-4" data-testid="provider-detail">
          <div className="flex items-start justify-between">
            <div>
              <button onClick={() => setSelectedProvider(null)} className="text-[#64748b] hover:text-white text-sm flex items-center gap-1 mb-3 transition-colors">
                <ArrowLeft className="h-3.5 w-3.5" /> Retour a la liste
              </button>
              <div className="flex items-center gap-3">
                <div className="h-14 w-14 rounded-xl bg-[#0099CC]/10 flex items-center justify-center text-[#0099CC] text-xl font-bold">
                  {selectedProvider.name.charAt(0)}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{selectedProvider.name}</h2>
                  <div className="flex items-center gap-3 text-sm text-[#64748b] mt-0.5">
                    <span className="text-[#0099CC] font-medium">{selectedProvider.category}</span>
                    {selectedProvider.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{selectedProvider.city}</span>}
                    {selectedProvider.featured && <span className="bg-[#F59E0B]/20 text-[#F59E0B] text-xs px-2 py-0.5 rounded-full font-medium">Recommande</span>}
                  </div>
                </div>
              </div>
            </div>
            <Button data-testid="contact-provider-btn" onClick={() => setShowContact(true)} className="bg-[#0099CC] hover:bg-[#0088B5] text-white">
              <MessageSquare className="h-4 w-4 mr-2" /> Contacter
            </Button>
          </div>

          <p className="text-[#94A3B8] leading-relaxed">{selectedProvider.description}</p>

          {/* Services */}
          {selectedProvider.services?.length > 0 && (
            <div>
              <h3 className="text-white font-semibold text-sm mb-2">Services proposes</h3>
              <div className="flex flex-wrap gap-2">
                {selectedProvider.services.map(s => (
                  <span key={s} className="bg-[#151B2B] border border-[#2A3441] text-[#94A3B8] text-xs px-3 py-1.5 rounded-lg">{s}</span>
                ))}
              </div>
            </div>
          )}

          {/* Contact info */}
          <div className="grid sm:grid-cols-3 gap-3">
            {selectedProvider.phone && (
              <a href={`tel:${selectedProvider.phone}`} className="flex items-center gap-2 bg-[#151B2B] border border-[#2A3441] rounded-xl p-3 text-sm text-[#94A3B8] hover:text-white hover:border-[#0099CC]/40 transition-colors">
                <Phone className="h-4 w-4 text-[#0099CC]" /> {selectedProvider.phone}
              </a>
            )}
            {selectedProvider.email && (
              <a href={`mailto:${selectedProvider.email}`} className="flex items-center gap-2 bg-[#151B2B] border border-[#2A3441] rounded-xl p-3 text-sm text-[#94A3B8] hover:text-white hover:border-[#0099CC]/40 transition-colors">
                <Mail className="h-4 w-4 text-[#0099CC]" /> {selectedProvider.email}
              </a>
            )}
            {selectedProvider.website && (
              <a href={selectedProvider.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-[#151B2B] border border-[#2A3441] rounded-xl p-3 text-sm text-[#94A3B8] hover:text-white hover:border-[#0099CC]/40 transition-colors">
                <Globe className="h-4 w-4 text-[#0099CC]" /> Site web
              </a>
            )}
          </div>

          {/* Rating */}
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map(i => (
              <Star key={i} className={`h-4 w-4 ${i <= selectedProvider.rating ? "text-[#F59E0B] fill-[#F59E0B]" : "text-[#2A3441]"}`} />
            ))}
            <span className="text-xs text-[#64748b] ml-1">{selectedProvider.rating}/5</span>
          </div>
        </div>
      )}

      {/* Contact Form */}
      {showContact && selectedProvider && (
        <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-6 space-y-4" data-testid="contact-form">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-white">Contacter {selectedProvider.name}</h2>
            <button onClick={() => setShowContact(false)} className="text-[#64748b] hover:text-white"><X className="h-5 w-5" /></button>
          </div>
          <textarea
            data-testid="contact-message"
            value={contactMsg}
            onChange={e => setContactMsg(e.target.value)}
            placeholder={`Bonjour ${selectedProvider.name},\n\nJe souhaite obtenir un devis pour...`}
            className="w-full h-32 bg-[#151B2B] border border-[#2A3441] text-white rounded-xl p-4 resize-none focus:outline-none focus:border-[#0099CC] text-sm"
          />
          <div className="flex gap-3">
            <Button data-testid="send-contact-btn" onClick={sendContactRequest} disabled={sending || !contactMsg.trim()} className="bg-[#0099CC] hover:bg-[#0088B5] text-white">
              <Send className="h-4 w-4 mr-2" /> {sending ? "Envoi..." : "Envoyer"}
            </Button>
            <Button variant="outline" onClick={() => setShowContact(false)} className="border-[#2A3441] text-[#94A3B8]">Annuler</Button>
          </div>
        </div>
      )}

      {/* Providers Grid */}
      {!selectedProvider && (
        <>
          {filtered.length === 0 ? (
            <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-12 text-center">
              <Building2 className="h-12 w-12 text-[#2A3441] mx-auto mb-3" />
              <p className="text-[#64748b]">{search || selectedCategory ? "Aucun prestataire trouve" : "Aucun prestataire disponible"}</p>
              <p className="text-[#4A5568] text-sm mt-1">Les prestataires seront bientot disponibles dans l'annuaire.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map(p => (
                <button
                  key={p.id}
                  data-testid={`provider-card-${p.id}`}
                  onClick={() => setSelectedProvider(p)}
                  className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-5 text-left hover:border-[#0099CC]/40 transition-all group"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="h-11 w-11 rounded-xl bg-[#0099CC]/10 flex items-center justify-center text-[#0099CC] font-bold text-lg">
                      {p.name.charAt(0)}
                    </div>
                    {p.featured && (
                      <span className="bg-[#F59E0B]/20 text-[#F59E0B] text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                        <Award className="h-3 w-3" /> Top
                      </span>
                    )}
                  </div>
                  <h3 className="text-white font-semibold group-hover:text-[#0099CC] transition-colors">{p.name}</h3>
                  <div className="flex items-center gap-2 mt-1 text-xs text-[#64748b]">
                    <span className="text-[#0099CC]">{p.category}</span>
                    {p.city && <><span>·</span><span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{p.city}</span></>}
                  </div>
                  <p className="text-[#94A3B8] text-xs mt-2 line-clamp-2">{p.description}</p>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map(i => (
                        <Star key={i} className={`h-3 w-3 ${i <= p.rating ? "text-[#F59E0B] fill-[#F59E0B]" : "text-[#2A3441]"}`} />
                      ))}
                    </div>
                    <ChevronRight className="h-4 w-4 text-[#4A5568] group-hover:text-[#0099CC] transition-colors" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
